import { CreateLoanRequest } from '@axo/shared/data-access/types/loan_request';
import { ApplicationDataFormValues } from './ApplicationDataFormSchema';
import { useCallback, useContext } from 'react';
import { useLoanApplication } from '@axo/shared/data-access/hooks';
import {
  CarOwnerShipType,
  Debtor,
  ExpenseKind,
} from '@axo/shared/data-access/types';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { getLocalPartOfEmail } from '../../../utils/getLocalPartOfEmail';
import { Broker } from '@axo/shared/data-access/types/broker';

export function useCreateLoanRequestFromFormValues(applicationID: string) {
  const { data: applicationData } = useLoanApplication(applicationID);
  const { state } = useContext(DataAccessContext);

  const hasCoApplicant =
    applicationData?.CoApplicantPersonID &&
    applicationData.CoApplicantCustomerID;

  return useCallback(
    (lenderID: string | null, values: ApplicationDataFormValues) => {
      if (!applicationData) return undefined;

      const coApplicantFields = hasCoApplicant
        ? {
            CoApplicantCustomerID: applicationData.CoApplicantCustomerID,
            CoApplicantPersonID: applicationData.CoApplicantPersonID,
            CoApplicantCitizenship: values.CoApplicantCitizenship,
            CoApplicantLivedInMarketCountrySince:
              values.CoApplicantLivedInMarketCountrySince,
            CoApplicantEducation: values.CoApplicantEducation,
            CoApplicantEmployer: values.CoApplicantEmployer,
            CoApplicantEmploymentStatus: values.CoApplicantEmploymentStatus,
            CoApplicantEmploymentStatusSince:
              values.CoApplicantEmploymentStatusSince,
            CoApplicantEmploymentStatusUntil:
              values.CoApplicantEmploymentStatusUntil,
            CoApplicantGrossIncomeYearly: values.CoApplicantGrossIncomeYearly,
            CoApplicantNetIncomeYearly: values.CoApplicantNetIncomeYearly,
            CoApplicantLivingCostMonthly: values.CoApplicantLivingCostMonthly,
            CoApplicantMaritalStatus: values.CoApplicantMaritalStatus,
            CoApplicantPartnerGrossIncomeYearly:
              values.CoApplicantPartnerGrossIncomeYearly,
            CoApplicantIsCohabitant: values.CoApplicantIsCohabitant,
            CoApplicantHousingType: values.CoApplicantHousingType,
            CoApplicantRentCostMonthly: values.CoApplicantRentCostMonthly,
            CoDebts: values.CoDebts.filter((debt) => Boolean(debt.Type)).map(
              (debt) => ({
                ID: null as never,
                Type: debt.Type as never,
                Creditor: debt.Creditor ?? undefined,
                MonthlyDebtPayment: debt.MonthlyDebtPayment ?? undefined,
                NominalInterestRate: debt.NominalInterestRate ?? undefined,
                OutstandingAmount: debt.OutstandingAmount,
                RefinanceAmount: debt.RefinanceAmount ?? undefined,
                Source: debt.Source ?? undefined,
                Debtor: Debtor.CoApplicant,
                LoanApplicationID: applicationID,
              }),
            ),
            CoApplicantResidencePermit: values.CoApplicantResidencePermit,
            CoApplicantResidencePermitType:
              values.CoApplicantResidencePermitType,
            CoApplicantResidencePermitNumber:
              values.CoApplicantResidencePermitNumber,
            CoApplicantResidencePermitExpiration:
              values.CoApplicantResidencePermitExpiration,
            CoApplicantHasUnemploymentFund:
              values.CoApplicantHasUnemploymentFund,
            CoApplicantCompanyOrganizationNumber:
              values.CoApplicantCompanyOrganizationNumber,
          }
        : {};

      return {
        ...coApplicantFields,

        Duration: values.LoanDuration ?? 0,
        Purpose: values.Purpose,
        OtherPurpose: values.OtherPurpose ?? undefined,
        Amount: values.AppliedAmount ?? 0,
        ApplicantMode: values.ApplicantMode,
        LenderID: lenderID ? lenderID : '',
        LoanApplicationID: applicationID,
        MarketCountry: applicationData.MarketCountry,
        Product: applicationData.Product,
        CreatedBy: getLocalPartOfEmail(state.user.email),
        Broker: applicationData.Meta?.Broker ?? Broker.Axo,

        CustomerID: applicationData.CustomerID,
        PersonID: applicationData.PersonID,

        MainBank: values.MainBank ?? undefined,

        Citizenship: values.Citizenship,
        LivedInMarketCountrySince: values.LivedInMarketCountrySince,
        Education: values.Education,
        MilitaryServiceCompleted: values.MilitaryServiceCompleted,
        PoliticallyInfluential: values.PoliticallyInfluential,
        EmploymentStatus: values.EmploymentStatus,
        Employer: values.Employer,
        EmployerIndustry: values.EmployerIndustry,
        EmploymentStatusSince: values.EmploymentStatusSince,
        EmploymentStatusUntil: values.EmploymentStatusUntil,
        JobTitle: values.JobTitle,
        JobPosition: values.JobPosition,
        GrossIncomeMonthly: values.GrossIncomeMonthly,
        GrossIncomeYearly: values.GrossIncomeYearly,
        NetIncomeMonthly: values.NetIncomeMonthly,
        ExtraIncomeMonthly: values.ExtraIncomeMonthly,
        ExtraIncomeRental: values.ExtraIncomeRental,
        ExtraIncomeChildSupport: values.ExtraIncomeChildSupport,
        LivingCostMonthly: values.LivingCostMonthly,
        MaritalStatus: values.MaritalStatus,
        PartnerGrossIncomeMonthly: values.PartnerGrossIncomeMonthly,
        PartnerGrossIncomeYearly: values.PartnerGrossIncomeYearly,
        PartnerNetIncomeYearly: values.PartnerNetIncomeYearly,
        PartnerLivingCostMonthly: values.PartnerLivingCostMonthly,
        HousingType: values.HousingType,
        ApartmentType: values.ApartmentType,
        RentCostMonthly: values.RentCostMonthly,
        OwnerAssociationCostMonthly: values.OwnerAssociationCostMonthly,
        // eslint-disable-next-line @eslint-react/no-children-map -- False positive
        Children: values.Children.map((child) => ({
          ID: null as never,
          Age: child.Age ?? 0,
          LoanApplicationID: applicationID,
        })),
        Debts: values.Debts.filter((debt) => Boolean(debt.Type)).map(
          (debt) => ({
            ID: null as never,
            Type: debt.Type as never,
            Creditor: debt.Creditor ?? undefined,
            MonthlyDebtPayment: debt.MonthlyDebtPayment ?? undefined,
            NominalInterestRate: debt.NominalInterestRate ?? undefined,
            OutstandingAmount: debt.OutstandingAmount,
            RefinanceAmount: debt.RefinanceAmount ?? undefined,
            Source: debt.Source ?? undefined,
            Debtor: Debtor.MainApplicant,
            LoanApplicationID: applicationID,
          }),
        ),
        CompanyOrganizationNumber: values.CompanyOrganizationNumber,
        ResidencePermit: values.ResidencePermit,
        ResidencePermitType: values.ResidencePermitType,
        ResidencePermitNumber: values.ResidencePermitNumber,
        ResidencePermitExpiration: values.ResidencePermitExpiration,
        HasUnemploymentFund: values.HasUnemploymentFund,
        YearsAsBankCustomer: values.YearsAsBankCustomer,
        HasDankort: values.HasDankort,
        HasOtherPaymentCards: values.HasOtherPaymentCards,
        CurrentYearDebtOutstandingAmount:
          values.CurrentYearDebtOutstandingAmount,
        OwnsHolidayHome: values.OwnsHolidayHome,
        HasCars: values.HasCars,
        Cars: values.HasCars
          ? [
              {
                ID: null as never,
                LoanApplicationID: applicationID,
                OwnershipType: values.CarOwnerShipType as CarOwnerShipType,
                TotalCostMonthly: values.CarTotalCostMonthly ?? 0,
              },
            ]
          : [],
        Expenses: [
          values.ExpensesPhoneInternetTV
            ? {
                ID: null as never,
                Kind: ExpenseKind.TVPhoneInternet,
                LoanApplicationID: applicationID,
                Value: values.ExpensesPhoneInternetTV,
              }
            : null,
          values.ExpensesTransport
            ? {
                ID: null as never,
                Kind: ExpenseKind.Transport,
                LoanApplicationID: applicationID,
                Value: values.ExpensesTransport,
              }
            : null,
          values.ExpensesElectricity
            ? {
                ID: null as never,
                Kind: ExpenseKind.Electricity,
                LoanApplicationID: applicationID,
                Value: values.ExpensesElectricity,
              }
            : null,
          values.ExpensesWater
            ? {
                ID: null as never,
                Kind: ExpenseKind.Water,
                LoanApplicationID: applicationID,
                Value: values.ExpensesWater,
              }
            : null,
          values.ExpensesHeating
            ? {
                ID: null as never,
                Kind: ExpenseKind.Heating,
                LoanApplicationID: applicationID,
                Value: values.ExpensesHeating,
              }
            : null,
          values.ExpensesInsurances
            ? {
                ID: null as never,
                Kind: ExpenseKind.Insurance,
                LoanApplicationID: applicationID,
                Value: values.ExpensesInsurances,
              }
            : null,
        ].filter((x) => x !== null),
      } satisfies CreateLoanRequest;
    },
    [applicationData, applicationID, state.user.email, hasCoApplicant],
  );
}
